/**
 * Підсумки: чотири показники, кожен — окрема скляна плитка (.tile) з тінню.
 *
 * РЕДИЗАЙН: 4 незалежні плитки замість однієї суцільної. Усередині кожної —
 * два періоди («За години …» та «За добу»), розділені тонкою лінією, без
 * вкладених боксів. Період позначено Lucide-іконкою (Clock — накопичувально до
 * поточної закритої години; CalendarDays — повна доба) + підпис-уточнення
 * (до 15:00 / 24 год). Заголовки — Lucide-іконки у кольорі показника (догма).
 *
 * «Виконання прогнозу, %» рахує бекенд ВІД СУМ (Σфакт / Σпрогноз): це виконання
 * плану, а не точність моделі — погодинне відношення при прогнозі близькому до
 * нуля дає сотні відсотків (у живих даних траплялось 604,9%).
 */
import { useTranslation } from 'react-i18next';
import i18n from '@/i18n';
import { LineChart, Zap, Gauge, Send, Clock, CalendarDays, type LucideIcon } from 'lucide-react';

export interface TileTotals {
  forecastKwh: number | null;
  factKwh: number | null;
  mmsKwh: number | null;
  performancePct: number | null;
}

interface Props {
  day: TileTotals | null;
  closed: (TileTotals & { throughHour: number }) | null;
  loading: boolean;
  mmsNote?: string | null;
}

// Кольори показників — догма (генерація→зелений, увага→бурштин, план→приглушений
// брендовий, MMS→нейтральний слейт) через токени: Lucide-іконки успадковують
// currentColor, тож var(--…) працює напряму й реагує на зміну теми.
const C_FORECAST = 'var(--series-3)';
const C_FACT = 'var(--ok)';
const C_PERF = 'var(--warn)';
const C_MMS = 'var(--series-2)';

const kwh = (v: number | null | undefined) =>
  v == null ? '—' : Number(v).toLocaleString(i18n.language === 'en' ? 'en-GB' : 'uk-UA', { minimumFractionDigits: 1, maximumFractionDigits: 1 });
const pct = (v: number | null | undefined) =>
  v == null ? '—' : `${Number(v).toLocaleString(i18n.language === 'en' ? 'en-GB' : 'uk-UA', { minimumFractionDigits: 1, maximumFractionDigits: 1 })}%`;

export default function ForecastMmsTiles({ day, closed, loading }: Props) {
  const { t } = useTranslation();
  const COLS: {
    key: keyof TileTotals; title: string; Icon: LucideIcon; color: string; unit: string;
    fmt: (v: number | null | undefined) => string;
  }[] = [
    { key: 'forecastKwh', title: t('ak_dsp_generation_forecast'), Icon: LineChart, color: C_FORECAST, unit: t('ak_dsp_kwh'), fmt: kwh },
    { key: 'factKwh', title: t('ak_dsp_actual_generation'), Icon: Zap, color: C_FACT, unit: t('ak_dsp_kwh'), fmt: kwh },
    { key: 'performancePct', title: t('ak_dsp_forecast_performance'), Icon: Gauge, color: C_PERF, unit: '', fmt: pct },
    { key: 'mmsKwh', title: t('ak_dsp_uploaded_to_mms'), Icon: Send, color: C_MMS, unit: t('ak_dsp_kwh'), fmt: kwh },
  ];

  const ROWS: { label: string; sub: string; data: TileTotals | null; PIcon: LucideIcon }[] = [
    {
      label: t('ak_dsp_by_hours'),
      sub: closed ? t('ak_dsp_until_00', { h: String(closed.throughHour).padStart(2, '0') }) : t('ak_dsp_cumulative'),
      data: closed,
      PIcon: Clock,
    },
    { label: t('ak_dsp_per_day'), sub: t('ak_dsp_24_h'), data: day, PIcon: CalendarDays },
  ];

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-3"
      style={{ opacity: loading ? 0.55 : 1, transition: 'opacity .15s', fontFamily: 'Urbanist, Inter, sans-serif' }}>
      {COLS.map((c) => (
        <div key={c.title} className="tile p-4 flex flex-col" style={{ minHeight: 158 }}>
          {/* Заголовок: Lucide-іконка у кольорі показника + назва */}
          <div className="flex items-center gap-2 mb-1">
            <c.Icon size={16} strokeWidth={2} style={{ color: c.color }} />
            <span className="text-[12.5px] font-semibold truncate" style={{ color: 'var(--text)' }} title={c.title}>{c.title}</span>
          </div>

          {/* Два періоди: іконка+підпис ліворуч, значення праворуч */}
          <div className="flex flex-col mt-1">
            {ROWS.map((r, ri) => {
              const raw = r.data ? r.data[c.key] : null;
              return (
                <div key={r.label}
                  className="flex items-center justify-between gap-2 py-2.5"
                  style={ri > 0 ? { borderTop: '1px solid var(--line)' } : undefined}>
                  <div className="flex items-center gap-1.5 min-w-0">
                    <r.PIcon size={13} strokeWidth={1.9} style={{ color: 'var(--text-dim)' }} />
                    <div className="flex flex-col leading-tight min-w-0">
                      <span className="text-[11.5px] font-semibold truncate" style={{ color: 'var(--text-muted)' }}>{r.label}</span>
                      <span className="text-[10px] leading-tight" style={{ color: 'var(--text-dim)' }}>{r.sub}</span>
                    </div>
                  </div>
                  <div className="flex items-baseline gap-1 shrink-0">
                    <span className="font-display text-[19px] font-normal leading-none"
                      style={{ color: raw == null ? 'var(--text-dim)' : 'var(--text)' }}>{c.fmt(raw)}</span>
                    {c.unit && <span className="text-[9.5px]" style={{ color: 'var(--text-muted)' }}>{c.unit}</span>}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
}
